import type { FC, PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react'

import IsAnonymousProvider from '@context/is-anonymous'
import {
  getStoredPref,
  removeStoredPref,
  setStoredPref,
} from '@lib/localstorage'
import { generateDBId } from '@motif/shared/db/fauna'
import type {
  File,
  Folder,
  GPTChatItem,
  Project,
  SidebarView,
  Space,
  User,
} from '@motif/types'

export const STORE_KEY_CURRENT_USER_ID = 'STORE_KEY_CURRENT_USER_ID'
export const STORE_KEY_CURRENT_SPACE_ID = 'STORE_KEY_CURRENT_SPACE_ID'
export const STORE_KEY_CURRENT_PROJECT_BY_SPACE =
  'STORE_KEY_CURRENT_PROJECT_BY_SPACE'
export const STORE_KEY_OPEN_FOLDERS_BY_PROJECT =
  'STORE_KEY_OPEN_FOLDERS_BY_PROJECT'
export const STORE_KEY_OPEN_COMPONENT_FILES_BY_PROJECT =
  'STORE_KEY_OPEN_COMPONENT_FILES_BY_PROJECT'
export const STORE_KEY_SIDEBAR_VIEW_BY_PROJECT =
  'STORE_KEY_SIDEBAR_VIEW_BY_PROJECT'
export const STORE_KEY_GPT_CHAT_ITEMS_BY_PROJECT =
  'STORE_KEY_GPT_CHAT_ITEMS_BY_PROJECT'
export const STORE_KEY_AUTO_SYNC_BY_PROJECT = 'STORE_KEY_AUTO_SYNC_BY_PROJECT'
export const STORE_KEY_SHOW_DEBUG_IDS = 'STORE_KEY_SHOW_DEBUG_IDS'

interface InternalReducerStateWithAnonymous extends State {
  isAnonymous: boolean
}

export interface State {
  currentUserId?: User['id']
  currentSpaceId?: Space['id']
  storeCurrentProjectId?: Project['id']
  currentProjectBySpace: Record<Space['id'], Project['id']>
  openFoldersByProject: Record<Project['id'], Folder['id'][]>
  openComponentFilesByProject: Record<Project['id'], File['id'][]>
  sidebarViewByProject: Record<Project['id'], SidebarView>
  gptChatItemsByProject: Record<Project['id'], GPTChatItem[]>
  autoSyncByProject: Record<Project['id'], boolean>
  showDebugIds?: boolean
  setCurrentUserId: (id?: User['id']) => void
  setCurrentSpaceId: (id?: Space['id']) => void
  setCurrentProjectInSpace: (
    spaceId: Space['id'],
    projectId?: Project['id']
  ) => void
  setFolderStateInProject: (
    projectId: Project['id'],
    folderId: Folder['id'],
    open: boolean
  ) => void
  setComponentFileStateInProject: (
    projectId: Project['id'],
    fileId: File['id'],
    open: boolean
  ) => void
  setSidebarViewInProject: (projectId: Project['id'], view: SidebarView) => void
  setGPTChatItemsInProject: (
    projectId: Project['id'],
    chatItems: GPTChatItem[]
  ) => void
  setAutoSyncInProject: (projectId: Project['id'], autoSync: boolean) => void
  setShowDebugIds: (show: boolean) => void
}

const initialState: InternalReducerStateWithAnonymous = {
  isAnonymous: false,
  currentUserId: undefined,
  currentSpaceId: undefined,
  showDebugIds: false,
  currentProjectBySpace: {},
  openFoldersByProject: {},
  openComponentFilesByProject: {},
  sidebarViewByProject: {},
  gptChatItemsByProject: {},
  autoSyncByProject: {},
  setCurrentUserId: () => {
    /**/
  },
  setCurrentSpaceId: () => {
    /**/
  },
  setCurrentProjectInSpace: () => {
    /**/
  },
  setFolderStateInProject: () => {
    /**/
  },
  setComponentFileStateInProject: () => {
    /**/
  },
  setSidebarViewInProject: () => {
    /**/
  },
  setGPTChatItemsInProject: () => {
    /**/
  },
  setAutoSyncInProject: () => {
    /**/
  },
  setShowDebugIds: () => {
    /**/
  },
}

type Action =
  | {
      type: 'INIT_STATE'
      isAnonymous: boolean
      currentUserId?: User['id']
      currentSpaceId?: Space['id']
      currentProjectBySpace: Record<Space['id'], Project['id']>
      openFoldersByProject: Record<Project['id'], Folder['id'][]>
      openComponentFilesByProject: Record<Project['id'], File['id'][]>
      sidebarViewByProject: Record<Project['id'], SidebarView>
      gptChatItemsByProject: Record<Project['id'], GPTChatItem[]>
      autoSyncByProject: Record<Project['id'], boolean>
      showDebugIds: boolean
    }
  | {
      type: 'SET_CURRENT_USER_ID'
      isAnonymous: boolean
      id?: User['id']
    }
  | {
      type: 'SET_CURRENT_SPACE_ID'
      isAnonymous: boolean
      id?: Space['id']
    }
  | {
      type: 'SET_CURRENT_PROJECT_IN_SPACE'
      isAnonymous: boolean
      spaceId: Space['id']
      projectId: Project['id'] | undefined
    }
  | {
      type: 'SET_FOLDER_STATE_IN_PROJECT'
      isAnonymous: boolean
      projectId: Project['id']
      folderId: Folder['id']
      open: boolean
    }
  | {
      type: 'SET_COMPONENT_FILE_STATE_IN_PROJECT'
      isAnonymous: boolean
      projectId: Project['id']
      fileId: File['id']
      open: boolean
    }
  | {
      type: 'SET_SIDEBAR_VIEW_IN_PROJECT'
      isAnonymous: boolean
      projectId: Project['id']
      view: SidebarView
    }
  | {
      type: 'SET_GPT_CHAT_ITEMS_IN_PROJECT'
      isAnonymous: boolean
      projectId: Project['id']
      chatItems: GPTChatItem[]
    }
  | {
      type: 'SET_AUTO_SYNC_IN_PROJECT'
      isAnonymous: boolean
      projectId: Project['id']
      autoSync: boolean
    }
  | {
      type: 'SET_SHOW_DEBUG_IDS'
      show?: boolean
    }

const storeReducer = (
  state: InternalReducerStateWithAnonymous,
  action: Action
): InternalReducerStateWithAnonymous => {
  switch (action.type) {
    case 'INIT_STATE': {
      return {
        ...state,
        isAnonymous: action.isAnonymous,
        currentUserId: action.currentUserId,
        currentSpaceId: action.currentSpaceId,
        currentProjectBySpace: action.currentProjectBySpace,
        openFoldersByProject: action.openFoldersByProject,
        openComponentFilesByProject: action.openComponentFilesByProject,
        sidebarViewByProject: action.sidebarViewByProject,
        gptChatItemsByProject: action.gptChatItemsByProject,
        autoSyncByProject: action.autoSyncByProject,
        showDebugIds: action.showDebugIds,
      }
    }
    case 'SET_CURRENT_USER_ID': {
      if (action.id) {
        setStoredPref(
          STORE_KEY_CURRENT_USER_ID,
          JSON.stringify(action.id),
          action.isAnonymous
        )
      } else {
        removeStoredPref(STORE_KEY_CURRENT_USER_ID, action.isAnonymous)
      }
      return { ...state, currentUserId: action.id }
    }
    case 'SET_CURRENT_SPACE_ID': {
      if (action.id) {
        setStoredPref(
          STORE_KEY_CURRENT_SPACE_ID,
          JSON.stringify(action.id),
          action.isAnonymous
        )
      } else {
        removeStoredPref(STORE_KEY_CURRENT_SPACE_ID, action.isAnonymous)
      }
      return {
        ...state,
        currentSpaceId: action.id,
      }
    }
    case 'SET_CURRENT_PROJECT_IN_SPACE': {
      let map = state.currentProjectBySpace
      if (!action.projectId) {
        delete map[action.spaceId]
      } else {
        map = {
          ...state.currentProjectBySpace,
          [action.spaceId]: action.projectId,
        }
      }
      setStoredPref(
        STORE_KEY_CURRENT_PROJECT_BY_SPACE,
        JSON.stringify(map),
        action.isAnonymous
      )
      return { ...state, currentProjectBySpace: map }
    }
    case 'SET_FOLDER_STATE_IN_PROJECT': {
      let openFolders = state.openFoldersByProject[action.projectId] || []
      const exists = openFolders.find((f) => f === action.folderId)
      if (action.open && !exists) {
        openFolders = [...openFolders, action.folderId]
      } else if (!action.open && exists) {
        openFolders = openFolders.filter((f) => f !== action.folderId)
      }
      const map = {
        ...state.openFoldersByProject,
        [action.projectId]: openFolders,
      }
      setStoredPref(
        STORE_KEY_OPEN_FOLDERS_BY_PROJECT,
        JSON.stringify(map),
        action.isAnonymous
      )
      return {
        ...state,
        openFoldersByProject: map,
      }
    }
    case 'SET_COMPONENT_FILE_STATE_IN_PROJECT': {
      let openComponentFiles =
        state.openComponentFilesByProject[action.projectId] || []
      const exists = openComponentFiles.find((f) => f === action.fileId)
      if (action.open && !exists) {
        openComponentFiles = [...openComponentFiles, action.fileId]
      } else if (!action.open && exists) {
        openComponentFiles = openComponentFiles.filter(
          (f) => f !== action.fileId
        )
      }
      const map = {
        ...state.openComponentFilesByProject,
        [action.projectId]: openComponentFiles,
      }
      setStoredPref(
        STORE_KEY_OPEN_COMPONENT_FILES_BY_PROJECT,
        JSON.stringify(map),
        action.isAnonymous
      )
      return {
        ...state,
        openComponentFilesByProject: map,
      }
    }
    case 'SET_SIDEBAR_VIEW_IN_PROJECT': {
      const map = {
        ...state.sidebarViewByProject,
        [action.projectId]: action.view,
      }
      setStoredPref(
        STORE_KEY_SIDEBAR_VIEW_BY_PROJECT,
        JSON.stringify(map),
        action.isAnonymous
      )
      return {
        ...state,
        sidebarViewByProject: map,
      }
    }
    case 'SET_GPT_CHAT_ITEMS_IN_PROJECT': {
      const map = {
        ...state.gptChatItemsByProject,
        [action.projectId]: action.chatItems,
      }
      setStoredPref(
        STORE_KEY_GPT_CHAT_ITEMS_BY_PROJECT,
        JSON.stringify(map),
        action.isAnonymous
      )
      return {
        ...state,
        gptChatItemsByProject: map,
      }
    }
    case 'SET_AUTO_SYNC_IN_PROJECT': {
      const map = {
        ...state.autoSyncByProject,
        [action.projectId]: action.autoSync,
      }
      setStoredPref(
        STORE_KEY_AUTO_SYNC_BY_PROJECT,
        JSON.stringify(map),
        action.isAnonymous
      )
      return {
        ...state,
        autoSyncByProject: map,
      }
    }
    case 'SET_SHOW_DEBUG_IDS': {
      setStoredPref(STORE_KEY_SHOW_DEBUG_IDS, action.show, false)
      return {
        ...state,
        showDebugIds: !!action.show,
      }
    }
  }
}

const StoreProvider = (props: PropsWithChildren) => {
  const [state, dispatch] = useReducer(storeReducer, initialState)
  const { isAnonymous } = IsAnonymousProvider.useContainer()

  const setCurrentUserId = useCallback(
    (id?: User['id']) => {
      dispatch({ type: 'SET_CURRENT_USER_ID', id, isAnonymous })
    },
    [isAnonymous]
  )

  const setCurrentSpaceId = useCallback(
    (id?: Space['id']) => {
      dispatch({ type: 'SET_CURRENT_SPACE_ID', id, isAnonymous })
    },
    [isAnonymous]
  )

  const setCurrentProjectInSpace = useCallback(
    (spaceId: Space['id'], projectId?: Project['id']) => {
      dispatch({
        type: 'SET_CURRENT_PROJECT_IN_SPACE',
        spaceId,
        projectId,
        isAnonymous,
      })
    },
    [isAnonymous]
  )

  const setFolderStateInProject = useCallback(
    (projectId: Project['id'], folderId: Folder['id'], open: boolean) => {
      dispatch({
        type: 'SET_FOLDER_STATE_IN_PROJECT',
        projectId,
        folderId,
        open,
        isAnonymous,
      })
    },
    [isAnonymous]
  )

  const setComponentFileStateInProject = useCallback(
    (projectId: Project['id'], fileId: File['id'], open: boolean) => {
      dispatch({
        type: 'SET_COMPONENT_FILE_STATE_IN_PROJECT',
        projectId,
        fileId,
        open,
        isAnonymous,
      })
    },
    [isAnonymous]
  )

  const setSidebarViewInProject = useCallback(
    (projectId: Project['id'], view: SidebarView) => {
      dispatch({
        type: 'SET_SIDEBAR_VIEW_IN_PROJECT',
        projectId,
        view,
        isAnonymous,
      })
    },
    [isAnonymous]
  )

  const setGPTChatItemsInProject = useCallback(
    (projectId: Project['id'], chatItems: GPTChatItem[]) => {
      dispatch({
        type: 'SET_GPT_CHAT_ITEMS_IN_PROJECT',
        projectId,
        chatItems,
        isAnonymous,
      })
    },
    [isAnonymous]
  )

  const setAutoSyncInProject = useCallback(
    (projectId: Project['id'], autoSync: boolean) => {
      dispatch({
        type: 'SET_AUTO_SYNC_IN_PROJECT',
        projectId,
        autoSync,
        isAnonymous,
      })
    },
    [isAnonymous]
  )

  const setShowDebugIds = useCallback((show: boolean) => {
    dispatch({
      type: 'SET_SHOW_DEBUG_IDS',
      show,
    })
  }, [])

  const value: State = useMemo(() => {
    const storeCurrentProjectId =
      state.currentSpaceId &&
      state.currentProjectBySpace?.[state.currentSpaceId]

    return {
      ...state,
      storeCurrentProjectId,
      setCurrentUserId,
      setCurrentSpaceId,
      setCurrentProjectInSpace,
      setFolderStateInProject,
      setComponentFileStateInProject,
      setSidebarViewInProject,
      setGPTChatItemsInProject,
      setAutoSyncInProject,
      setShowDebugIds,
    }
  }, [
    state,
    setCurrentUserId,
    setCurrentSpaceId,
    setCurrentProjectInSpace,
    setFolderStateInProject,
    setComponentFileStateInProject,
    setSidebarViewInProject,
    setGPTChatItemsInProject,
    setAutoSyncInProject,
    setShowDebugIds,
  ])

  useEffect(() => {
    const initStore = () => {
      // Set initial values from storage in one go
      let currentUserId: string | undefined = getStoredPref(
        STORE_KEY_CURRENT_USER_ID,
        undefined,
        isAnonymous
      )

      const currentSpaceId: string | undefined = getStoredPref(
        STORE_KEY_CURRENT_SPACE_ID,
        undefined,
        isAnonymous
      )

      const currentProjectBySpace = getStoredPref(
        STORE_KEY_CURRENT_PROJECT_BY_SPACE,
        {},
        isAnonymous
      )

      if (isAnonymous && !currentUserId) {
        // Hack to set an auto-generated user id in the app, and
        // in localstorage (for subsequent loads) if the user is
        // using an anonymous Motif instance. Also sets the
        // (unique) active space and project.
        currentUserId = `anonymous-${generateDBId()}`

        setStoredPref(
          STORE_KEY_CURRENT_USER_ID,
          JSON.stringify(currentUserId),
          true
        )
      }

      dispatch({
        type: 'INIT_STATE',
        isAnonymous: isAnonymous,
        currentUserId,
        currentSpaceId,
        currentProjectBySpace,
        openFoldersByProject: getStoredPref(
          STORE_KEY_OPEN_FOLDERS_BY_PROJECT,
          {},
          isAnonymous
        ),
        openComponentFilesByProject: getStoredPref(
          STORE_KEY_OPEN_COMPONENT_FILES_BY_PROJECT,
          {},
          isAnonymous
        ),
        sidebarViewByProject: getStoredPref(
          STORE_KEY_SIDEBAR_VIEW_BY_PROJECT,
          {},
          isAnonymous
        ),
        gptChatItemsByProject: getStoredPref(
          STORE_KEY_GPT_CHAT_ITEMS_BY_PROJECT,
          {},
          isAnonymous
        ),
        autoSyncByProject: getStoredPref(
          STORE_KEY_AUTO_SYNC_BY_PROJECT,
          {},
          isAnonymous
        ),
        showDebugIds: getStoredPref(
          STORE_KEY_SHOW_DEBUG_IDS,
          false,
          isAnonymous
        ),
      })
    }

    // initial initialization of the store
    initStore()

    const onStorage = () => {
      // Update state when storage changes (e.g. in another tab)
      initStore()
    }

    // when the storage event is triggered, update the state
    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener('storage', onStorage)
    }
  }, [isAnonymous])

  return <StoreContext.Provider value={value} {...props} />
}

export const useStore = (): State => {
  const context = useContext(StoreContext)
  if (context === undefined) {
    throw new Error(`useStore must be used within a StoreProvider`)
  }
  return context
}

export const StoreContext = createContext<State>(initialState)

StoreContext.displayName = 'StoreContext'

export const ManagedStoreContext: FC<PropsWithChildren> = ({ children }) => (
  <StoreProvider>{children}</StoreProvider>
)
